import { z } from "zod";

import { entityIdSchema, isoInstantSchema } from "./primitives";

/**
 * Audit log (SPEC §61).
 *
 * Append-only by convention: rows are never updated or deleted, and the table
 * deliberately has no foreign key to `sites` so that the trail survives a site
 * being removed.
 */

/** Actions worth recording (SPEC §61, TASK-903). */
export const auditActionSchema = z.enum([
  "login",
  "create_entry",
  "update_entry",
  "delete_entry",
  "create_localization",
  "update_localization",
  "delete_localization",
  "create_translation",
  "publish",
  "unpublish",
  "schedule",
  "upload_media",
  "delete_media",
  "change_theme",
  "change_site_config",
  "change_password",
  "create_taxonomy",
  "update_taxonomy",
  "delete_taxonomy",
  "create_llm_provider",
  "update_llm_provider",
  "delete_llm_provider",
  "verify_llm_provider",
  "github_sync",
  "github_webhook",
  "manual_rebuild",
]);

export type AuditAction = z.infer<typeof auditActionSchema>;

export const auditResourceTypeSchema = z.enum([
  "site",
  "entry",
  "localization",
  "draft",
  "media",
  "theme",
  "discussion",
  "deployment",
  "index",
  "llm_provider",
]);

export type AuditResourceType = z.infer<typeof auditResourceTypeSchema>;

/**
 * Keys that must never be persisted to an audit record.
 *
 * `metadata` is free-form, so this is enforced at write time rather than by the
 * schema: a leaked token in the audit log is still a leaked token
 * (AGENTS.md §49, SPEC §70).
 */
export const AUDIT_REDACTED_KEYS: readonly string[] = [
  "token",
  "accessToken",
  "authorization",
  "cookie",
  "privateKey",
  "secret",
  "password",
  "cfAccessJwt",
];

export const auditLogSchema = z
  .object({
    id: entityIdSchema,
    siteId: entityIdSchema,

    /** Who acted: a Cloudflare Access email, or `system` for cron/webhook work. */
    actor: z.string().min(1).max(320),
    action: auditActionSchema,

    resourceType: auditResourceTypeSchema.optional(),
    resourceId: z.string().max(200).optional(),

    metadata: z.record(z.unknown()).optional(),

    createdAt: isoInstantSchema,
  })
  .strict();

export type AuditLog = z.infer<typeof auditLogSchema>;

/**
 * Strip anything that looks like a credential from audit metadata.
 *
 * Applied recursively, because nested objects are the usual way a secret sneaks
 * into a log line.
 */
export function redactAuditMetadata(value: unknown, depth = 0): unknown {
  if (depth > 6 || value === null || typeof value !== "object") {
    return value;
  }

  if (Array.isArray(value)) {
    // `Array.isArray` narrows `unknown` to `any[]`; cast back so the recursion
    // stays typed rather than leaking `any`.
    return (value as unknown[]).map((item) => redactAuditMetadata(item, depth + 1));
  }

  const out: Record<string, unknown> = {};

  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    out[key] = AUDIT_REDACTED_KEYS.some((blocked) => key.toLowerCase() === blocked.toLowerCase())
      ? "[redacted]"
      : redactAuditMetadata(entry, depth + 1);
  }

  return out;
}
